'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Form as BootstrapForm,
  Container,
  Row,
  Col,
} from 'react-bootstrap';
import { createOperation, updateOperation } from '../../../../services/OperationService.js';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../../common/Toast/toast';
import { useAuth } from '../../../../context/AuthContext';
import './OperationForm.css';

// The rule that turns a label into its key. Named because three places
// need to agree on it: the label's onChange, the "use the generated one"
// link, and the check for whether the slug is still the derived one.
const slugify = (label) => String(label || '').toLowerCase().trim().replace(/\s+/g, '-');

const MAX_LEN = 50;

// Validation schema using Yup
const validationSchema = Yup.object({
  label: Yup.string()
    .required('Label is required')
    .min(2, 'Label must be at least 2 characters')
    .max(50, 'Label must not exceed 50 characters'),
  value: Yup.string()
    .required('Value is required')
    .min(2, 'Value must be at least 2 characters')
    .max(50, 'Value must not exceed 50 characters'),
  // .matches(/^[a-zA-Z0-9_-]+$/, "Value can only contain letters, numbers, hyphens, and underscores"),
});

// Renders as a page on /setting/Add-opreation and as the body of a dialog
// when ListOpreation opens it. Which operation is being edited comes from
// `record` in a dialog and from location.state on the route -- this form
// never had an :id param to fall back on.
function AddOperation({
  // Shown inside a dialog rather than as a page.
  embedded = false,
  // The operation to edit. Falls back to location.state.operation.
  record,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
}) {
  const [alert, setAlert] = useState({
    show: false,
    variant: 'success',
    message: '',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const hasShownToast = useRef(false);

  // Check if we're in edit mode
  const operationData = record ?? location.state?.operation ?? null;
  const isEdit = Boolean(record) || location.state?.isEdit || false;

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/list-opreation');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/list-opreation');
  };

  // Dynamic initial values based on edit mode
  const getInitialValues = () => {
    if (isEdit && operationData) {
      return {
        label: operationData.label || '',
        value: operationData.value || '',
      };
    }
    return {
      label: '',
      value: '',
    };
  };

  const showAlert = (variant, message) => {
    let msgStr = '';
    if (typeof message === 'object' && message !== null) {
      msgStr = message.message || message.ariaLabel || JSON.stringify(message);
    } else {
      msgStr = String(message || '');
    }
    setAlert({ show: true, variant, message: msgStr });
    // Auto hide alert after 5 seconds
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      let response;

      if (isEdit && operationData?.id) {
        // Update existing operation
        response = await updateOperation(operationData.id, values);
        showAlert('success', response?.message || 'Operation updated successfully!');
      } else {
        // Create new operation
        response = await createOperation(values);
        showAlert('success', response?.message || 'Operation added successfully!');
      }

      // The dialog stays open after a save; a new operation's fields are
      // emptied so a second Save is not the same operation twice.
      if (!isEdit) resetForm();
      // Navigate after a short delay so user can see the alert
      setTimeout(finish, 0);
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'adding'} operation:`, error);
      let errorMessage = `Failed to ${isEdit ? 'update' : 'add'} operation`;
      if (error.response?.data?.message) {
        if (typeof error.response.data.message === 'object') {
          errorMessage = error.response.data.message.message || error.response.data.message.ariaLabel || JSON.stringify(error.response.data.message);
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      showAlert('danger', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
      if (!hasPermission('operations', 'add')) {
        if (!hasShownToast.current) {
          toast.error('You do not have permission to add operations.');
          navigate('/admin');
          hasShownToast.current = true;
        }
      }
    }, [hasPermission, navigate]);

  /* ── The dialog build ───────────────────────────────────────────────
     Its own tree rather than the page's. The two fields are not
     independent -- `value` is the key `label` generates -- and the page
     markup presents them as two unrelated boxes, which leaves the reader
     guessing why the second one fills itself in. Here the pair is the
     design: the slug is tied to the label, says whether it is still the
     generated one, and offers its way back when it is not.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="opf">
        {alert.show && (
          <div className={`opf-note opf-note--${alert.variant}`} role="status">
            <span className="opf-note-icon" aria-hidden="true">
              {alert.variant === 'success' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v5M12 16.5v.01" />
                </svg>
              )}
            </span>
            <span className="opf-note-text">{alert.message}</span>
            <button
              type="button"
              className="opf-note-close"
              onClick={() => setAlert((prev) => ({ ...prev, show: false }))}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => {
            // The slug still matches what the label would generate, so it
            // can go on tracking it. Once it does not, the label stops
            // overwriting it -- the typed key is the reader's, not ours.
            const derived = slugify(values.label);
            const isAuto = values.value === derived;
            const labelBad = touched.label && errors.label;
            const valueBad = touched.value && errors.value;
            const countClass = (v) => {
              const n = String(v || '').length;
              if (n > MAX_LEN) return ' opf-count--over';
              if (n > MAX_LEN - 10) return ' opf-count--near';
              return '';
            };

            return (
              <Form noValidate>
                <div className="opf-field">
                  <div className="opf-label-row">
                    <label className="opf-label" htmlFor="opf-label">
                      Label<span className="opf-req">*</span>
                    </label>
                    <span className={`opf-count${countClass(values.label)}`}>
                      {String(values.label || '').length}/{MAX_LEN}
                    </span>
                  </div>

                  <Field name="label">
                    {({ field, form }) => (
                      <input
                        {...field}
                        id="opf-label"
                        type="text"
                        className={`opf-input${labelBad ? ' opf-input--bad' : ''}`}
                        placeholder="Delete, Export, Approve…"
                        disabled={isSubmitting}
                        autoComplete="off"
                        onChange={(e) => {
                          const next = e.target.value;
                          form.setFieldValue('label', next);
                          // Only while the key is still the generated one.
                          if (isAuto) form.setFieldValue('value', slugify(next));
                        }}
                      />
                    )}
                  </Field>

                  {labelBad ? (
                    <p className="opf-error">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v5M12 16.5v.01" />
                      </svg>
                      {errors.label}
                    </p>
                  ) : (
                    <p className="opf-hint">
                      How this operation reads wherever permissions are listed.
                    </p>
                  )}
                </div>

                <div className="opf-field opf-derived">
                  <div className="opf-label-row">
                    <label className="opf-label" htmlFor="opf-value">
                      Value<span className="opf-req">*</span>
                      {isAuto ? (
                        <span className="opf-state opf-state--auto">From label</span>
                      ) : (
                        <>
                          <span className="opf-state opf-state--custom">Edited</span>
                          {derived && (
                            <button
                              type="button"
                              className="opf-relink"
                              onClick={() => setFieldValue('value', derived)}
                            >
                              use {derived}
                            </button>
                          )}
                        </>
                      )}
                    </label>
                    <span className={`opf-count${countClass(values.value)}`}>
                      {String(values.value || '').length}/{MAX_LEN}
                    </span>
                  </div>

                  <Field name="value">
                    {({ field }) => (
                      <input
                        {...field}
                        id="opf-value"
                        type="text"
                        className={`opf-input opf-input--slug${valueBad ? ' opf-input--bad' : ''}`}
                        placeholder="delete"
                        disabled={isSubmitting}
                        autoComplete="off"
                        spellCheck="false"
                      />
                    )}
                  </Field>

                  {valueBad ? (
                    <p className="opf-error">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v5M12 16.5v.01" />
                      </svg>
                      {errors.value}
                    </p>
                  ) : (
                    <p className="opf-hint">
                      The key the permission checks are written against. Changing
                      it on an operation already in use breaks those checks.
                    </p>
                  )}
                </div>

                <footer className="opf-foot">
                  <button
                    type="button"
                    className="opf-btn"
                    onClick={leave}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="opf-btn opf-btn--main"
                    disabled={isSubmitting}
                  >
                    {isSubmitting && <span className="opf-spin" aria-hidden="true" />}
                    {isSubmitting
                      ? isEdit
                        ? 'Updating…'
                        : 'Adding…'
                      : isEdit
                        ? 'Update operation'
                        : 'Add operation'}
                  </button>
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <Card.Title className="mb-0">
                  {isEdit ? 'Edit Operation' : 'Add New Operation'}
                </Card.Title>
                {/* The page's way back. In a dialog the close button and
                    Cancel are already that, and this one would navigate
                    out from under the dialog. */}
                {!embedded && (
                  <Link to="/setting/list-opreation">
                    <Button variant="outline-secondary" size="sm">
                      <i className="fa fa-arrow-left me-2" />
                      Back to List
                    </Button>
                  </Link>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  dismissible
                  onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
                  className="mb-3"
                >
                  {alert.message}
                </Alert>
              )}

              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true} // Important: This allows form to reinitialize when props change
              >
                {({ isSubmitting, errors, touched, resetForm }) => (
                  <Form>
                    <BootstrapForm.Group className="mb-3">
                      <BootstrapForm.Label htmlFor="label">
                        Label <span className="text-danger">*</span>
                      </BootstrapForm.Label>
                      {/* <Field
                        as={BootstrapForm.Control}
                        type="text"
                        id="label"
                        name="label"
                        placeholder="Enter operation label"
                        isInvalid={touched.label && errors.label}
                        disabled={isSubmitting}
                      /> */}
                      <Field name="label">
                        {({ field, form }) => (
                          <BootstrapForm.Control
                            type="text"
                            id="label"
                            placeholder="Enter operation label"
                            isInvalid={touched.label && errors.label}
                            disabled={isSubmitting}
                            {...field}
                            onChange={(e) => {
                              const newLabel = e.target.value;
                              form.setFieldValue('label', newLabel);
                              const transformedValue = newLabel.toLowerCase().replace(/\s+/g, '-');
                              form.setFieldValue('value', transformedValue);
                            }}
                          />
                        )}
                      </Field>

                      <ErrorMessage name="label">
                        {(msg) => (
                          <BootstrapForm.Control.Feedback type="invalid">
                            {msg}
                          </BootstrapForm.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </BootstrapForm.Group>

                    <BootstrapForm.Group className="mb-3">
                      <BootstrapForm.Label htmlFor="value">
                        Value <span className="text-danger">*</span>
                      </BootstrapForm.Label>
                      <Field
                        as={BootstrapForm.Control}
                        type="text"
                        id="value"
                        name="value"
                        placeholder="Enter operation value"
                        isInvalid={touched.value && errors.value}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage name="value">
                        {(msg) => (
                          <BootstrapForm.Control.Feedback type="invalid">
                            {msg}
                          </BootstrapForm.Control.Feedback>
                        )}
                      </ErrorMessage>
                      {/* <BootstrapForm.Text className="text-muted">
                        Value should be lowercase and contain only letters, numbers, hyphens, and underscores.
                      </BootstrapForm.Text> */}
                    </BootstrapForm.Group>

                    <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                      <Button
                        variant="outline-secondary"
                        type="button"
                        disabled={isSubmitting}
                        onClick={leave}
                      >
                        Cancel
                      </Button>
                      <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {isEdit ? 'Updating...' : 'Adding...'}
                          </>
                        ) : isEdit ? (
                          'Update Operation'
                        ) : (
                          'Add Operation'
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default AddOperation;
